/**
 * Footer.jsx - Premium 2026 (FIXED)
 * 
 * - ALL 10 divisions with WORKING links
 * - Prominent OmniAlera banner
 * - Mobile responsive
 * 
 * @version 2.1.2
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Droplets, ExternalLink, Heart, Clock, Mail, Linkedin, Globe,
  ChevronRight, Building, ArrowUpRight
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Handle external link click
  const handleExternalClick = (url, e) => {
    e.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const footerLinks = {
    product: [
      { name: 'CBC Analysis', path: '/analyzers/cbc' },
      { name: 'CMP Analysis', path: '/analyzers/cmp' },
      { name: 'Coagulation', path: '/analyzers/coagulation' },
      { name: 'Telemedicine', path: '/telemedicine', badge: 'Pro' },
      { name: 'VR Simulation', path: '/vr-simulation', badge: 'Beta' },
      { name: 'Pricing', path: '/pricing' }
    ],
    company: [
      { name: 'About the Founder', path: '/about-founder' },
      { name: 'OmniAlera Technologies', url: 'https://omnialera.com', external: true }
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy', comingSoon: true },
      { name: 'Terms of Service', path: '/terms', comingSoon: true },
      { name: 'HIPAA Compliance', path: '/hipaa', comingSoon: true },
      { name: 'Medical Disclaimer', path: '/medical-disclaimer', comingSoon: true }
    ],
    // ALL 10 DIVISIONS with correct URLs
    divisions: [
      { name: 'OmniAlera Medical', url: 'https://omnialera-medical-platform.netlify.app', division: 1 },
      { name: 'MedCloudMentor', url: 'https://medcloudmentor.netlify.app/', division: 2 },
      { name: 'OmniClinicalBridge', url: 'https://omniclinicalbridge-v2.netlify.app/', division: 3 },
      { name: 'RadiantScanAI', url: 'https://radiantscan-ai.netlify.app', division: 4 },
      { name: 'HemaAI', url: 'https://hema-ai.netlify.app', division: 5, current: true },
      { name: 'ApexVitalsAI', url: 'https://apexvitals-ai.netlify.app', division: 6 },
      { name: 'GenomicCareOS', url: 'https://genomiccare-os.netlify.app', division: 7 },
      { name: 'HospitalOpsAI', url: 'https://hospitalops-ai.netlify.app', division: 8 },
      { name: 'PharmaRealTime', url: 'https://pharma-realtime.netlify.app', division: 9 },
      { name: 'DiseaseAtlasAI', url: 'https://disease-atlas-ai.netlify.app', division: 10 }
    ]
  };

  return (
    <footer className="bg-gray-900 text-white font-sans">
      {/* OmniAlera Main Site Banner */}
      <div className="bg-gradient-to-r from-red-700 to-red-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <a
            href="https://omnialera.com"
            onClick={(e) => handleExternalClick('https://omnialera.com', e)}
            className="flex items-center justify-center gap-2 sm:gap-3 text-white hover:text-red-100 transition-colors group cursor-pointer"
          >
            <Globe className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="font-medium text-sm sm:text-base">Visit OmniAlera Technologies Holdings Inc.</span>
            <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </a>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 sm:gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-red-900/30">
                <Droplets className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
              </div>
              <div>
                <span className="text-xl sm:text-2xl font-bold">HemaAI</span>
                <span className="text-xs text-gray-400 block">v2.0</span>
              </div>
            </Link>
            <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6 max-w-xs">
              AI-powered hematology analysis with telemedicine and physician verification. 
              Part of the OmniAlera Healthcare Empire.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-2 sm:gap-3">
              <a href="mailto:ceo@omnialera.com" className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg sm:rounded-xl transition-colors" title="Email">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              </a>
              <a 
                href="https://www.linkedin.com/in/omokayode-aderolu-04b25026/" 
                onClick={(e) => handleExternalClick('https://www.linkedin.com/in/omokayode-aderolu-04b25026/', e)}
                className="p-2 bg-gray-800 hover:bg-blue-600 rounded-lg sm:rounded-xl transition-colors cursor-pointer" 
                title="LinkedIn"
              >
                <Linkedin className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              </a>
              <a 
                href="https://omnialera.com" 
                onClick={(e) => handleExternalClick('https://omnialera.com', e)}
                className="p-2 bg-gray-800 hover:bg-red-600 rounded-lg sm:rounded-xl transition-colors cursor-pointer" 
                title="OmniAlera"
              >
                <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="font-semibold mb-3 sm:mb-4 text-white text-sm sm:text-base">Product</h4>
            <ul className="space-y-2 sm:space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="text-gray-300 hover:text-white text-xs sm:text-sm transition-colors inline-flex items-center gap-1 sm:gap-2">
                    {link.name}
                    {link.badge && (
                      <span className="text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 bg-gray-700 rounded-full text-gray-300">{link.badge}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company & Legal */}
          <div>
            <h4 className="font-semibold mb-3 sm:mb-4 text-white text-sm sm:text-base">Company</h4>
            <ul className="space-y-2 sm:space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  {link.external ? (
                    <a 
                      href={link.url} 
                      onClick={(e) => handleExternalClick(link.url, e)}
                      className="text-gray-300 hover:text-white text-xs sm:text-sm transition-colors inline-flex items-center gap-1 cursor-pointer"
                    >
                      {link.name}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <Link to={link.path} className="text-gray-300 hover:text-white text-xs sm:text-sm transition-colors">
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            
            <h4 className="font-semibold mb-3 sm:mb-4 mt-4 sm:mt-6 text-white text-sm sm:text-base">Legal</h4>
            <ul className="space-y-2 sm:space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="text-gray-300 hover:text-white text-xs sm:text-sm transition-colors inline-flex items-center gap-1 sm:gap-2">
                    {link.name}
                    {link.comingSoon && <Clock className="w-3 h-3 text-amber-500" />}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ALL 10 OmniAlera Divisions */}
          <div className="col-span-2 md:col-span-1">
            <h4 className="font-semibold mb-3 sm:mb-4 text-white flex items-center gap-2 text-sm sm:text-base">
              <Building className="w-4 h-4 text-red-500" />
              All 10 Divisions
            </h4>
            <ul className="space-y-1 sm:space-y-2">
              {footerLinks.divisions.map((platform) => (
                <li key={platform.name}>
                  <a
                    href={platform.url}
                    onClick={(e) => handleExternalClick(platform.url, e)}
                    className={`text-xs sm:text-sm transition-colors inline-flex items-center gap-1 cursor-pointer ${
                      platform.current 
                        ? 'text-red-400 font-medium' 
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    <span className="text-[10px] sm:text-xs text-gray-300 w-4">{platform.division}.</span>
                    {platform.name}
                    {platform.current ? (
                      <span className="text-[10px] bg-red-600 px-1 py-0.5 rounded ml-1">Here</span>
                    ) : (
                      <ExternalLink className="w-3 h-3" />
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Mission Statement */}
      <div className="border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 text-center">
          <p className="text-xs sm:text-sm text-gray-400 flex items-center justify-center gap-2">
            Made with <Heart className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" /> for African Healthcare
          </p>
          <p className="text-[10px] sm:text-xs text-gray-300 mt-2">
            Transforming healthcare through AI-powered diagnostics
          </p>
        </div>
      </div>

      {/* Back to OmniAlera */}
      <div className="border-t border-gray-800 bg-gray-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <p className="text-xs sm:text-sm text-gray-400">
              © {currentYear} OmniAlera Technologies Holdings Inc.
            </p>
            <a
              href="https://omnialera.com"
              onClick={(e) => handleExternalClick('https://omnialera.com', e)}
              className="text-xs sm:text-sm text-red-400 hover:text-red-300 transition-colors inline-flex items-center gap-2 font-medium cursor-pointer"
            >
              Part of the OmniAlera Healthcare Empire
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <p className="text-[10px] sm:text-xs text-gray-300 text-center">
            HemaAI is a clinical decision support tool, not a diagnostic device. 
            Always consult a qualified healthcare provider.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
